import { Drawer, Form, Input, InputNumber, Switch, Space, Button, App } from 'antd';
import { useEffect } from 'react';
import { useUpdateLanguage } from '@/hooks/useLanguages';
import type { Language } from '@/types/language';

interface Props {
  open: boolean;
  language: Language | null;
  onClose: () => void;
}

export function EditLanguageDrawer({ open, language, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<{
    name_native: string;
    sort_order: number;
    enabled: boolean;
  }>();
  const mutation = useUpdateLanguage();

  useEffect(() => {
    // Only touch the form while the drawer is open, so setFieldsValue never runs
    // against an unmounted (unconnected) <Form> instance.
    if (open && language)
      form.setFieldsValue({
        name_native: language.name_native,
        sort_order: language.sort_order,
        enabled: language.enabled,
      });
  }, [open, language, form]);

  async function save() {
    if (!language) return;
    const vals = await form.validateFields();
    try {
      await mutation.mutateAsync({ code: language.code, input: vals });
      message.success('Saved');
      onClose();
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  return (
    <Drawer
      open={open}
      title={language ? `Edit ${language.code}` : 'Edit'}
      onClose={onClose}
      width={420}
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={save} loading={mutation.isPending}>
            Save
          </Button>
        </Space>
      }
    >
      {language && (
        <Form form={form} layout="vertical">
          <Form.Item label="Code">
            <Input value={language.code} disabled />
          </Form.Item>
          <Form.Item label="Native name" name="name_native" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Sort order" name="sort_order">
            <InputNumber min={0} max={9999} />
          </Form.Item>
          <Form.Item label="Enabled" name="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      )}
    </Drawer>
  );
}
